import type { StorageBridge } from '@casa/bridge';
import { parseManifest, type DriverManifest, type DriverPermissions } from '@casa/driver-sdk';

/**
 * Drivers the user installs (proposal §6.3): a folder with `driver.json` and the bundled module it
 * names. Installing records the user's consent to the manifest's permissions and pins the module's
 * SHA-256; loading refuses a module whose hash differs. The gate uses the consented permissions.
 *
 * In the app's storage, per driver: `drivers/<id>/driver.json`, `drivers/<id>/<entry>`, and
 * `drivers/<id>/install.json` (the `InstallRecord`), written last, so a half-written install is
 * never loaded.
 */

declare const TextEncoder: new () => { encode(text: string) : Uint8Array };
declare const TextDecoder: new (label?: string, options?: { fatal?: boolean }) => { decode(bytes: Uint8Array) : string };
declare const crypto: { subtle: { digest(algorithm: string, data: Uint8Array) : Promise<ArrayBuffer> } };

export const DRIVERS_FOLDER = 'drivers';
export const MAX_DRIVER_CODE_BYTES = 5 * 1024 * 1024;
const MANIFEST_FILE = 'driver.json';
const RECORD_FILE = 'install.json';

export interface DriverFile {
    /** The file's name, without folders. */
    name: string;
    bytes: Uint8Array;
}

export interface DriverPackage {
    manifest: DriverManifest;
    manifestBytes: Uint8Array;
    code: Uint8Array;
    /** Of `code`, lower-case hex. */
    sha256: string;
}

/** The user's consent to one version of a driver. */
export interface InstallRecord {
    id: string;
    name: string;
    version: string;
    /** The pinned hash of the module, lower-case hex. */
    sha256: string;
    /** What the user agreed to; the gate uses these, not the stored manifest. */
    permissions: DriverPermissions;
    /** ISO 8601. */
    consentedAt: string;
}

export type LoadedDriver =
    | { ok: true; record: InstallRecord; manifest: DriverManifest; code: string }
    | { ok: false; id: string; reason: string };

export class DriverPackageError extends Error {
    override name = 'DriverPackageError';
    readonly issues: readonly string[];

    constructor(message: string, issues: readonly string[] = [], options?: ErrorOptions) {
        super(message, options);
        this.issues = issues;
    }
}

export async function sha256Hex(bytes: Uint8Array) : Promise<string> {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));

    return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

const folderOf = (id: string) : string => `${DRIVERS_FOLDER}/${id}`;

function decodeUtf8(bytes: Uint8Array, what: string) : string {
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch (error) {
        throw new DriverPackageError(`${what} is not UTF-8 text.`, [], { cause: error });
    }
}

function parseJson(bytes: Uint8Array, what: string) : unknown {
    try {
        return JSON.parse(decodeUtf8(bytes, what)) as unknown;
    } catch (error) {
        if (error instanceof DriverPackageError) {
            throw error;
        }

        throw new DriverPackageError(`${what} is not valid JSON.`, [], { cause: error });
    }
}

/** Reads a driver folder's files; `reservedIds` are the built-in drivers' names, which it may not take. */
export async function readDriverPackage(files: readonly DriverFile[], reservedIds: ReadonlySet<string>) : Promise<DriverPackage> {
    const manifestFile = files.find((file) => file.name === MANIFEST_FILE);
    if (!manifestFile) {
        throw new DriverPackageError(`The folder has no ${MANIFEST_FILE}.`);
    }

    const parsed = parseManifest(parseJson(manifestFile.bytes, MANIFEST_FILE));
    if (!parsed.ok) {
        throw new DriverPackageError(`${MANIFEST_FILE} is not valid.`, parsed.issues);
    }

    const { manifest } = parsed;
    if (reservedIds.has(manifest.id)) {
        throw new DriverPackageError(`"${manifest.id}" is the name of a built-in driver.`);
    }

    const codeFile = files.find((file) => file.name === manifest.entry);
    if (!codeFile) {
        throw new DriverPackageError(`The folder has no ${manifest.entry}, which ${MANIFEST_FILE} names as the driver's module.`);
    }
    if (codeFile.bytes.length > MAX_DRIVER_CODE_BYTES) {
        throw new DriverPackageError(`${manifest.entry} is larger than ${MAX_DRIVER_CODE_BYTES / 1024 / 1024} MB.`);
    }
    decodeUtf8(codeFile.bytes, manifest.entry);

    return { manifest, manifestBytes: manifestFile.bytes, code: codeFile.bytes, sha256: await sha256Hex(codeFile.bytes) };
}

async function removeFolder(storage: StorageBridge, id: string) : Promise<void> {
    // The record goes first: without it, whatever is left is not loaded.
    await storage.remove(`${folderOf(id)}/${RECORD_FILE}`);
    for (const path of await storage.list(`${folderOf(id)}/`)) {
        await storage.remove(path);
    }
}

/** Installs (or replaces) a driver the user has consented to; returns the record of that consent. */
export async function installDriver(storage: StorageBridge, driverPackage: DriverPackage, consentedAt: Date) : Promise<InstallRecord> {
    const { manifest } = driverPackage;
    const record: InstallRecord = {
        id: manifest.id,
        name: manifest.name,
        version: manifest.version,
        sha256: driverPackage.sha256,
        permissions: manifest.permissions,
        consentedAt: consentedAt.toISOString(),
    };

    await removeFolder(storage, manifest.id);
    await storage.write(`${folderOf(manifest.id)}/${manifest.entry}`, driverPackage.code);
    await storage.write(`${folderOf(manifest.id)}/${MANIFEST_FILE}`, driverPackage.manifestBytes);
    await storage.write(`${folderOf(manifest.id)}/${RECORD_FILE}`, new TextEncoder().encode(`${JSON.stringify(record, null, 2)}\n`));

    return record;
}

export async function removeInstalledDriver(storage: StorageBridge, id: string) : Promise<void> {
    await removeFolder(storage, id);
}

async function readRecord(storage: StorageBridge, id: string) : Promise<InstallRecord | undefined> {
    const bytes = await storage.read(`${folderOf(id)}/${RECORD_FILE}`);

    return bytes ? (parseJson(bytes, RECORD_FILE) as InstallRecord) : undefined;
}

/** The ids of the installed drivers (those with an install record). */
export async function listInstalledDriverIds(storage: StorageBridge) : Promise<string[]> {
    const suffix = `/${RECORD_FILE}`;

    return (await storage.list(`${DRIVERS_FOLDER}/`))
        .filter((path) => path.endsWith(suffix) && path.split('/').length === 3)
        .map((path) => path.split('/')[1] ?? '')
        .sort();
}

/** Reads an installed driver and checks its module against the pinned hash. */
export async function loadInstalledDriver(storage: StorageBridge, id: string) : Promise<LoadedDriver> {
    try {
        const record = await readRecord(storage, id);
        if (!record) {
            return { ok: false, id, reason: 'not installed' };
        }

        const manifestBytes = await storage.read(`${folderOf(id)}/${MANIFEST_FILE}`);
        const parsed = manifestBytes ? parseManifest(parseJson(manifestBytes, MANIFEST_FILE)) : undefined;
        if (!parsed?.ok) {
            return { ok: false, id, reason: `its ${MANIFEST_FILE} is missing or not valid` };
        }

        const code = await storage.read(`${folderOf(id)}/${parsed.manifest.entry}`);
        if (!code) {
            return { ok: false, id, reason: `its module ${parsed.manifest.entry} is missing` };
        }
        if ((await sha256Hex(code)) !== record.sha256) {
            return { ok: false, id, reason: 'its module changed since it was installed; install it again' };
        }

        return { ok: true, record, manifest: parsed.manifest, code: decodeUtf8(code, parsed.manifest.entry) };
    } catch (error) {
        return { ok: false, id, reason: error instanceof Error ? error.message : String(error) };
    }
}

export async function loadInstalledDrivers(storage: StorageBridge) : Promise<LoadedDriver[]> {
    const loaded: LoadedDriver[] = [];
    for (const id of await listInstalledDriverIds(storage)) {
        loaded.push(await loadInstalledDriver(storage, id));
    }

    return loaded;
}
